// NPM
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useParams } from "react-router-dom";
import { omitBy, isNull, isEmpty, map } from "lodash";

// HELPERS
import { decode } from "Helpers/enc";

// UTILITY
import errorHandler from "Utils/error-handler";

// VALIDATOR
import InsuranceRateFormSchema from "./insurance-rate-form-validation";

// STYLE
import "./insurance-rate-form.scss";

// HOOKS
import useQuery from "Hooks/query.hook";

// ANT COMPONENTS
import { CloseOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { Col, Row, Grid } from "antd";
import { AlertBox, Button, DateRange, TextInput } from "@life-line-lab/components-react";

interface InsuranceRateFormComponentProps {
  info?: any;
  onSubmit?: any;
  onUpdate?: any;
}

const InsuranceRateFormComponent: React.FC<InsuranceRateFormComponentProps> = ({
  info,
  onSubmit,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const { useBreakpoint } = Grid;
  const screen = useBreakpoint();
  const code = useQuery().get("code");
  const { id } = useParams<{ id: string }>();

  const initialInput = {
    validFrom: {
      date: null,
      dateString: "",
    },
    validTo: {
      date: moment("31/12/9999", "DD/MM/YYYY"),
      dateString: moment("31/12/9999", "DD/MM/YYYY").toISOString(),
    },
    insuranceCharges: [
      {
        valueFrom: "",
        valueTo: "",
        charge: "",
      },
      {
        valueFrom: "",
        valueTo: "",
        charge: "",
      },
    ],
  };
  const initialError = {};

  const [input, setInput] = useState<any>(initialInput);
  const [error, setError] = useState<any>(initialError);

  useEffect(() => {
    document.body.classList.add("stack-screen--open");
    return () => document.body.classList.remove("stack-screen--open");
  }, []);

  useEffect(() => {
    if (info) {
      setInput({
        ...input,
        ...info,
        validFrom: { date: info?.validFrom, dateString: moment(info?.validFrom).toISOString() },
        validTo: { date: info?.validTo, dateString: moment(info?.validTo).toISOString() },
      });
    }
  }, [info]); // ensures the fields pre-filled when the tab is refreshed, else can set in initialInput

  // validate against overlapping value from and value to
  const handleOverlappingValues = (name: string, value: number, i: number) => {
    // if i is 0, this is the first row, so no risk of overlapping
    if (i == 0 && name == "valueFrom") {
      return false;
    }

    // if i >= 1, check i-1
    // if name == valueFrom, check that [i].valueFrom > [i-1].valueTo
    // if name == valueTo, check that [i].valueTo > [i].valueFrom (also prevented by validation)
    if (name == "valueFrom") {
      if (value > input.insuranceCharges[i - 1].valueTo) {
        return false;
      }
    }

    if (name == "valueTo") {
      if (value > input.insuranceCharges[i].valueFrom) {
        return false;
      }
    }

    return true;
  };

  const handleRowInputChange = (e: React.ChangeEvent<HTMLInputElement>, i: string) => {
    const { name, value } = e.currentTarget;

    let rateList = input.insuranceCharges;
    let newValues = JSON.parse(JSON.stringify(rateList)); // it will give just readOnly, so to solve this, we need this hehe -jazz
    newValues[i][name] = value;
    setInput({ ...input, insuranceCharges: newValues });

    // test for value for valueTo and valueFrom only
    if (name != "charge") {
      const valueText = name === "valueTo" ? "value from" : "value to";

      // rule: valueTo cannot be more than valueFrom, and there must absolutely be no overlapping values
      const isOverlapping = handleOverlappingValues(name, parseInt(value), parseInt(i));

      // if there is overlapping and a value is existent, set an error
      if (isOverlapping && value) {
        setError((prev) => ({
          ...prev,
          [`insuranceCharges[${i}].${name}`]: `Enter a number larger than the previous ${valueText}.`,
        }));
      } else {
        // otherwise, delete the specific error if it exists
        if (error?.[`insuranceCharges[${i}].${name}`]) {
          setError((prev) =>
            omitBy(
              {
                ...prev,
                [`insuranceCharges[${i}].${name}`]: null,
              },
              isNull,
            ),
          );
        }
      }
    } else {
      // reset any error that appeared
      setError(initialError);
    }
  };

  const handleDateChange = (startDate: any, endDate: any) => {
    if (startDate.date) {
      setError(initialError);
      setInput({ ...input, validFrom: startDate });
    } else if (endDate.date) {
      setError(initialError);
      setInput({ ...input, validTo: endDate });
    }
  };

  const addRows = () => {
    let insuranceCharges = input.insuranceCharges.concat({
      valueFrom: "",
      valueTo: "",
      charge: "",
    });
    setInput({ ...input, ["insuranceCharges"]: insuranceCharges });
  };

  const deleteRows = (index) => {
    let insuranceList = input.insuranceCharges;
    insuranceList.splice(index, 1);
    setError(initialError);
    setInput({ ...input, ["insuranceCharges"]: insuranceList });
  };

  const handleSubmit = () => {
    try {
      const valInput = {
        ...input,
        settingID: info?._id,
        code: decode(code as any),
        validFrom: moment(input?.validFrom?.date).toISOString(),
        validTo: moment(input?.validTo?.date).toISOString(),
      };

      const value = InsuranceRateFormSchema.validateSync(omitBy(valInput, isNull), {
        strict: false,
        abortEarly: true,
        stripUnknown: true,
      });

      info ? onUpdate(value) : onSubmit(value);
    } catch (err) {
      console.log(err);
      const { path, value }: any = errorHandler.validation(err);

      switch (value) {
        case "error.pattern":
          // get valueFrom or valueTo
          const substr = path.substring(path.indexOf(".") + 1);

          if (substr === "valueTo" || substr === "valueFrom") {
            setError({
              ...error,
              [path]: t(`error.pattern`, { field: substr }),
            });
          } else {
            setError({
              ...error,
              [path]: t(`error.pattern`, { field: t(`label.${path}`) }),
            });
          }

          break;
        case "error.validationFailed":
          setError({
            ...error,
            [path]: t(`error.required`, { field: t(`label.${path}`) }),
          });
          break;
        default:
          setError({
            ...error,
            [path]: t(`${value}`, { field: t(`label.${path}`) }),
          });
      }
    }
  };

  return (
    <div className="insurance-rate-form">
      <form>
        <div className="main-header">
          <span className="center header-title">{t("title.insuranceRate").toUpperCase()}</span>
        </div>
        <Row gutter={[15, 15]} className="mt-40">
          <Col xs={24} sm={24} md={24}>
            <Row gutter={18}>
              <Col xs={24} sm={24} md={21} offset={screen.md ? 1 : 0} className="mb-30">
                <DateRange
                  isRequired={true}
                  isError={!!error.identificationIssueDate}
                  startLabel={t("label.validFrom")}
                  endLabel={t("label.validTo")}
                  value={{
                    startDate: input.validFrom,
                    endDate: input.validTo,
                  }}
                  onChange={handleDateChange}
                />
                <AlertBox text={error.validFrom || error.validTo} />
              </Col>
            </Row>
            <Row gutter={18}>
              <Col xs={0} sm={0} md={7} className="mb-0" offset={screen.md ? 1 : 0}>
                <label className="inp-label inp-label--required">{t("label.valueFrom")}</label>
              </Col>
              <Col xs={0} sm={0} md={7} className="mb-0">
                <label className="inp-label inp-label--required">{t("label.valueTo")}</label>
              </Col>
              <Col xs={0} sm={0} md={7} className="mb-0">
                <label className="inp-label inp-label--required">{t("label.charge")}</label>
              </Col>
            </Row>
            {map(input.insuranceCharges, (ratesObj, i) => {
              return (
                <Row gutter={[12, 0]} key={i}>
                  <Col xs={24} sm={24} md={0} className="mb-0">
                    <label className="inp-label inp-label--required">{t(`label.valueFrom`)}</label>
                  </Col>
                  <Col xs={24} sm={24} md={7} offset={screen.md ? 1 : 0} className="mb-10">
                    <TextInput
                      isError={!!error.insuranceCharges}
                      isRequired={true}
                      placeholder={t("txt.rm")}
                      value={ratesObj.valueFrom}
                      name="valueFrom"
                      onChange={(e) => {
                        handleRowInputChange(e, i);
                      }}
                    />
                    <AlertBox text={error[`insuranceCharges[${i}].valueFrom`]} />
                  </Col>
                  <Col xs={24} sm={24} md={0} className="mb-0">
                    <label className="inp-label inp-label--required">{t(`label.valueTo`)}</label>
                  </Col>
                  <Col xs={24} sm={24} md={7} className="mb-10">
                    <TextInput
                      isRequired={true}
                      placeholder={t("txt.rm")}
                      value={ratesObj.valueTo}
                      name="valueTo"
                      onChange={(e) => {
                        handleRowInputChange(e, i);
                      }}
                    />
                    <AlertBox text={error[`insuranceCharges[${i}].valueTo`]} />
                  </Col>
                  <Col xs={24} sm={24} md={0} className="mb-0">
                    <label className="inp-label inp-label--required">{t(`label.charge`)}</label>
                  </Col>
                  <Col xs={24} sm={24} md={7} className="mb-10">
                    <TextInput
                      isRequired={true}
                      placeholder={t("txt.rm")}
                      value={ratesObj.charge}
                      name="charge"
                      onChange={(e) => {
                        handleRowInputChange(e, i);
                      }}
                    />
                    <AlertBox
                      text={
                        error[`insuranceCharges[${i}].charge`]
                          ? t(`error.pattern`, { field: t(`label.charge`) })
                          : ""
                      }
                    />
                  </Col>
                  {id ? (
                    <></>
                  ) : (
                    <Col md={1} className={screen.md ? "" : "mb-12"}>
                      {input?.insuranceCharges?.length > 1 ? (
                        <div className="mt-10 text-center">
                          <CloseOutlined onClick={() => deleteRows(i)} />
                        </div>
                      ) : null}
                    </Col>
                  )}
                </Row>
              );
            })}
            {id ? (
              <></>
            ) : (
              <Row>
                <Col
                  xs={24}
                  offset={screen.md ? 1 : 0}
                  className={`${screen.md ? "mt-20" : "mt-18"} mb-0`}
                >
                  <Button
                    type="primary"
                    label={t("action.addRow")}
                    size="large"
                    icon={<PlusCircleOutlined />}
                    onClick={() => {
                      addRows();
                    }}
                  />
                </Col>
              </Row>
            )}
            <Row className="mt-50" justify="center">
              <Col className="mb-50">
                <Button
                  size="large"
                  type="primary"
                  isDisabled={!isEmpty(error)}
                  onClick={handleSubmit}
                  label={id ? t("action.update") : t("action.create")}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </form>
    </div>
  );
};

export default InsuranceRateFormComponent;
